import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import Inputmask from 'inputmask';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useBeforeUnload, useKeyboardShortcut } from '../hooks';
import { useBusiness, usePayPeriod, useUpdateBusiness } from '../hooks/useApi';
import {
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Box,
  Tooltip,
  Modal,
  Avatar,
  Stack,
  Chip,
  Paper,
  alpha,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import PaymentsIcon from '@mui/icons-material/Payments';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import agent from '../api/axios';
import { formatDateDisplay } from '../utils/dateUtils';
import { US_STATES, PAY_FREQUENCIES } from '../utils/constants';
import { PageHeader } from '../components/ui';

// Days of the week options
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Yup validation schema
const schema = yup
  .object({
    name: yup.string().required('Company Name is required'),
    fein: yup
      .string()
      .matches(/^\d{2}-\d{7}$/, 'FEIN must be in format XX-XXXXXXX')
      .required('FEIN is required'),
    stateTaxId: yup.string().optional(),
    streetAddress: yup.string().required('Street Address is required'),
    address2: yup.string().optional(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zip: yup
      .string()
      .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP Code')
      .required('ZIP Code is required'),
    phoneNumber: yup.string().optional(),
    ownerEmail: yup
      .string()
      .email('Invalid email')
      .required('Email Address is required'),
    payFrequency: yup.string().required('Pay Frequency is required'),
    endOfWeek: yup
      .number()
      .min(0)
      .max(6)
      .required('Week ending day is required'),
    payCheckDelay: yup
      .number()
      .min(0)
      .max(14)
      .required('Pay check delay is required'),
  })
  .required();

type FormValues = {
  name: string;
  fein: string;
  stateTaxId?: string;
  streetAddress: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber?: string;
  ownerEmail: string;
  payFrequency: string;
  endOfWeek: number;
  payCheckDelay: number;
};

export default function Profile() {
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    trigger,
    control,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      fein: '',
      stateTaxId: '',
      streetAddress: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phoneNumber: '',
      ownerEmail: '',
      payFrequency: '',
      endOfWeek: 6,
      payCheckDelay: 1,
    },
  });

  const { data: business, isLoading: loading } = useBusiness();
  const { data: payPeriodInfo } = usePayPeriod();
  const updateMutation = useUpdateBusiness();

  const [editingOpen, setEditingOpen] = useState(false);
  const [editTab, setEditTab] = useState(0);
  const saving = updateMutation.isPending;

  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const feinInputRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Warn about unsaved changes
  useBeforeUnload(isDirty && editingOpen);

  // Keyboard shortcuts
  useKeyboardShortcut(
    { key: 's', ctrlKey: true },
    () => {
      if (editingOpen && isDirty) {
        handleSubmit(onSubmit)();
      }
    },
    editingOpen
  );

  useKeyboardShortcut(
    { key: 'Escape' },
    () => {
      if (editingOpen) {
        if (isDirty) {
          if (
            window.confirm(
              'You have unsaved changes. Are you sure you want to close?'
            )
          ) {
            setEditingOpen(false);
          }
        } else {
          setEditingOpen(false);
        }
      }
    },
    editingOpen
  );

  // Calculate form completion progress
  const watchedValues = watch();
  const requiredFields = [
    'name',
    'fein',
    'streetAddress',
    'city',
    'state',
    'zip',
    'ownerEmail',
    'payFrequency',
    'endOfWeek',
    'payCheckDelay',
  ];
  const filledFields = requiredFields.filter(
    (field) => !!watchedValues[field as keyof FormValues]
  );
  const completionProgress = Math.round(
    (filledFields.length / requiredFields.length) * 100
  );

  useEffect(() => {
    // Populate form when business data is loaded
    if (business) {
      const b = Array.isArray(business) ? business[0] : business;
      reset({
        name: b.name || '',
        fein: b.fein || '',
        stateTaxId: b.stateTaxId || '',
        streetAddress: b.streetAddress || '',
        address2: b.address2 || '',
        city: b.city || '',
        state: b.state || '',
        zip: b.zip || '',
        phoneNumber: b.phoneNumber || '',
        ownerEmail: b.ownerEmail || '',
        payFrequency: b.payFrequency || '',
        endOfWeek: b.endOfWeek ?? 6,
        payCheckDelay: b.payCheckDelay ?? 1,
      });
    }
  }, [business, reset]);

  // Apply input masks after form is populated
  useEffect(() => {
    if (phoneInputRef.current) {
      Inputmask({ mask: '(999) 999-9999', clearIncomplete: true }).mask(
        phoneInputRef.current
      );
    }
    if (feinInputRef.current) {
      Inputmask({ mask: '99-9999999', clearIncomplete: true }).mask(
        feinInputRef.current
      );
    }
    nameRef.current?.focus();
  }, []);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    updateMutation.mutate(data, {
      onSuccess: () => {
        setEditingOpen(false);
      },
    });
  };

  const openEditor = () => {
    // ensure form has current values before opening
    if (business) {
      reset({
        name: business.name || '',
        fein: business.fein || '',
        stateTaxId: business.stateTaxId || '',
        streetAddress: business.streetAddress || '',
        address2: business.address2 || '',
        city: business.city || '',
        state: business.state || '',
        zip: business.zip || '',
        phoneNumber: business.phoneNumber || '',
        ownerEmail: business.ownerEmail || '',
        payFrequency: business.payFrequency || '',
        endOfWeek: business.endOfWeek ?? 6,
        payCheckDelay: business.payCheckDelay ?? 1,
      });
    }
    setEditTab(0);
    setEditingOpen(true);
  };

  // Count errors per tab for badge display
  const companyErrors = ['name', 'fein', 'stateTaxId'].filter(
    (f) => errors[f as keyof FormValues]
  ).length;
  const addressErrors = [
    'streetAddress',
    'address2',
    'city',
    'state',
    'zip',
  ].filter((f) => errors[f as keyof FormValues]).length;
  const contactErrors = ['phoneNumber', 'ownerEmail'].filter(
    (f) => errors[f as keyof FormValues]
  ).length;
  const payrollErrors = ['payFrequency', 'endOfWeek', 'payCheckDelay'].filter(
    (f) => errors[f as keyof FormValues]
  ).length;

  return (
    <Box className="fade-in">
      <PageHeader
        title="Company Profile"
        subtitle="Manage your business information and payroll settings"
        icon={<BusinessIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={openEditor}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Edit Profile
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Company Overview Card */}
          <Grid item xs={12} lg={8}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              {/* Header with gradient */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
                  p: 3,
                  color: 'white',
                }}
              >
                <Stack direction="row" spacing={3} alignItems="center">
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      fontSize: '2rem',
                      fontWeight: 700,
                      border: '3px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    {business?.name ? business.name[0].toUpperCase() : '?'}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {business?.name || 'Company Name'}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip
                        label={
                          business?.fein ? `FEIN: ${business.fein}` : 'No FEIN'
                        }
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 500,
                          fontFamily: 'monospace',
                        }}
                      />
                      {business?.subscriptionName && (
                        <Chip
                          label={business.subscriptionName}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              {/* Details Grid */}
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Contact Information */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        mb: 2,
                        display: 'block',
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Stack spacing={2}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <EmailIcon color="primary" fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography sx={{ fontWeight: 500 }}>
                            {business?.ownerEmail || '—'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: (theme) =>
                              alpha(theme.palette.info.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PhoneIcon color="info" fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography sx={{ fontWeight: 500 }}>
                            {business?.phoneNumber || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        mb: 2,
                        display: 'block',
                      }}
                    >
                      Business Address
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: (theme) =>
                            alpha(theme.palette.warning.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LocationOnIcon color="warning" fontSize="small" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>
                          {business?.streetAddress || '—'}
                        </Typography>
                        {business?.address2 && (
                          <Typography color="text.secondary">
                            {business.address2}
                          </Typography>
                        )}
                        <Typography color="text.secondary">
                          {business?.city ? `${business.city}, ` : ''}
                          {business?.state || ''} {business?.zip || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Tax Information */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        mb: 2,
                        display: 'block',
                      }}
                    >
                      Tax Information
                    </Typography>
                    <Stack spacing={2}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: (theme) =>
                              alpha(theme.palette.secondary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BadgeIcon color="secondary" fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            State Tax ID
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 500, fontFamily: 'monospace' }}
                          >
                            {business?.stateTaxId || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Payroll Settings Card */}
          <Grid item xs={12} lg={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background:
                        'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PaymentsIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Payroll Schedule
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Payment frequency and dates
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                      border: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Pay Frequency
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        color: 'primary.main',
                      }}
                    >
                      {payPeriodInfo?.payFrequency ||
                        business?.payFrequency ||
                        '—'}
                    </Typography>
                  </Paper>

                  <Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CalendarMonthIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Week Ends On
                        </Typography>
                        <Typography sx={{ fontWeight: 500 }}>
                          {payPeriodInfo?.endOfWeekName || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CalendarMonthIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Current Pay Period
                        </Typography>
                        <Typography sx={{ fontWeight: 500 }}>
                          {payPeriodInfo?.currentPeriod
                            ? `${formatDateDisplay(
                                payPeriodInfo.currentPeriod.start
                              )} - ${formatDateDisplay(
                                payPeriodInfo.currentPeriod.end
                              )}`
                            : '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CalendarMonthIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Next Pay Check
                        </Typography>
                        <Typography sx={{ fontWeight: 500 }}>
                          {payPeriodInfo?.currentPeriod
                            ? formatDateDisplay(
                                payPeriodInfo.currentPeriod.payCheckDate
                              )
                            : '—'}
                          {payPeriodInfo?.payCheckDelay ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({payPeriodInfo.payCheckDelay}{' '}
                              {payPeriodInfo.payCheckDelay === 1
                                ? 'day'
                                : 'days'}{' '}
                              after period end)
                            </Typography>
                          ) : null}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Edit modal with the existing form */}
      <Modal
        open={editingOpen}
        onClose={() => {
          if (saving) return;
          if (isDirty) {
            if (
              window.confirm(
                'You have unsaved changes. Are you sure you want to close?'
              )
            ) {
              setEditingOpen(false);
            }
          } else {
            setEditingOpen(false);
          }
        }}
        aria-labelledby="edit-profile-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: 3,
            width: '90%',
            maxWidth: 800,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              color: 'white',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Edit Company Profile
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Update your business information and payroll settings
            </Typography>
            {/* Progress indicator */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LinearProgress
                variant="determinate"
                value={completionProgress}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 4,
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {completionProgress}% Complete
              </Typography>
            </Box>
          </Box>

          {/* Tabs for navigation */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: (theme) => theme.palette.custom.softBackground,
            }}
          >
            <Tabs
              value={editTab}
              onChange={(_, newValue) => setEditTab(newValue)}
              variant="fullWidth"
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    Company
                    {companyErrors > 0 && (
                      <Chip
                        label={companyErrors}
                        size="small"
                        color="error"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon fontSize="small" />
                    Address
                    {addressErrors > 0 && (
                      <Chip
                        label={addressErrors}
                        size="small"
                        color="error"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    Contact
                    {contactErrors > 0 && (
                      <Chip
                        label={contactErrors}
                        size="small"
                        color="error"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentsIcon fontSize="small" />
                    Payroll
                    {payrollErrors > 0 && (
                      <Chip
                        label={payrollErrors}
                        size="small"
                        color="error"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3, overflow: 'auto', flex: 1, minHeight: 350 }}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Tab 0: Company Details */}
              {editTab === 0 && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Enter your registered business name and tax identification
                    numbers.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Company Name"
                        {...register('name')}
                        fullWidth
                        autoComplete="organization"
                        error={!!errors.name}
                        helperText={
                          errors.name?.message ||
                          'Your registered business name'
                        }
                        onBlur={() => trigger('name')}
                        InputLabelProps={{ shrink: !!watch('name') }}
                        inputRef={(e) => {
                          register('name').ref(e);
                          nameRef.current = e;
                        }}
                        InputProps={{
                          endAdornment:
                            watch('name') && !errors.name ? (
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                              />
                            ) : errors.name ? (
                              <ErrorIcon color="error" fontSize="small" />
                            ) : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Tooltip title="Format: 12-3456789">
                        <TextField
                          label="FEIN (Employer ID)"
                          {...register('fein')}
                          inputRef={(e) => {
                            register('fein').ref(e);
                            feinInputRef.current = e;
                          }}
                          fullWidth
                          error={!!errors.fein}
                          helperText={
                            errors.fein?.message ||
                            'Federal Employer Identification Number'
                          }
                          onBlur={() => trigger('fein')}
                          InputLabelProps={{ shrink: !!watch('fein') }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="State Tax ID (Optional)"
                        {...register('stateTaxId')}
                        fullWidth
                        error={!!errors.stateTaxId}
                        helperText={
                          errors.stateTaxId?.message ||
                          'State tax identification number'
                        }
                        onBlur={() => trigger('stateTaxId')}
                        InputLabelProps={{ shrink: !!watch('stateTaxId') }}
                      />
                    </Grid>

                    {/* Logo Upload Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Company Logo (Optional)
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 2 }}
                      >
                        Upload a logo to display on invoices and reports (max
                        2MB, formats: JPEG, PNG, GIF, WebP, SVG)
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        {business?.logoUrl && (
                          <Avatar
                            src={business.logoUrl}
                            alt="Company Logo"
                            variant="rounded"
                            sx={{ width: 80, height: 80 }}
                          />
                        )}
                        <Box>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  await agent.Business.uploadLogo(file);
                                  // Force refetch business data
                                  window.location.reload();
                                  enqueueSnackbar(
                                    'Logo uploaded successfully',
                                    { variant: 'success' }
                                  );
                                } catch (error: any) {
                                  enqueueSnackbar(
                                    error.response?.data ||
                                      'Failed to upload logo',
                                    { variant: 'error' }
                                  );
                                }
                              }
                            }}
                          />
                          <label htmlFor="logo-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                            >
                              {business?.logoUrl
                                ? 'Change Logo'
                                : 'Upload Logo'}
                            </Button>
                          </label>
                          {business?.logoUrl && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              sx={{ ml: 1 }}
                              onClick={async () => {
                                try {
                                  await agent.Business.deleteLogo();
                                  // Force refetch business data
                                  window.location.reload();
                                  enqueueSnackbar('Logo removed successfully', {
                                    variant: 'success',
                                  });
                                } catch (error: any) {
                                  enqueueSnackbar('Failed to remove logo', {
                                    variant: 'error',
                                  });
                                }
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Tab 1: Address */}
              {editTab === 1 && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    This address will appear on 1099 forms sent to contractors.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Street Address"
                        {...register('streetAddress')}
                        autoComplete="street-address"
                        fullWidth
                        error={!!errors.streetAddress}
                        helperText={errors.streetAddress?.message}
                        onBlur={() => trigger('streetAddress')}
                        InputLabelProps={{ shrink: !!watch('streetAddress') }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Address Line 2 (Optional)"
                        {...register('address2')}
                        autoComplete="address-line2"
                        fullWidth
                        placeholder="Suite, Unit, Building, Floor, etc."
                        error={!!errors.address2}
                        helperText={errors.address2?.message}
                        onBlur={() => trigger('address2')}
                        InputLabelProps={{ shrink: !!watch('address2') }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="City"
                        {...register('city')}
                        autoComplete="address-level2"
                        fullWidth
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        onBlur={() => trigger('city')}
                        InputLabelProps={{ shrink: !!watch('city') }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth error={!!errors.state}>
                        <InputLabel id="state-label">State</InputLabel>
                        <Controller
                          name="state"
                          control={control}
                          render={({ field }) => (
                            <Select
                              labelId="state-label"
                              label="State"
                              {...field}
                              onBlur={() => trigger('state')}
                            >
                              {US_STATES.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="ZIP Code"
                        autoComplete="postal-code"
                        {...register('zip')}
                        fullWidth
                        error={!!errors.zip}
                        helperText={errors.zip?.message}
                        onBlur={() => trigger('zip')}
                        InputLabelProps={{ shrink: !!watch('zip') }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Tab 2: Contact */}
              {editTab === 2 && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Contact information for account notifications and support.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contact Email"
                        autoComplete="email"
                        {...register('ownerEmail')}
                        fullWidth
                        error={!!errors.ownerEmail}
                        helperText={
                          errors.ownerEmail?.message ||
                          'Used for account notifications'
                        }
                        onBlur={() => trigger('ownerEmail')}
                        InputLabelProps={{ shrink: !!watch('ownerEmail') }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone Number (Optional)"
                        {...register('phoneNumber')}
                        inputRef={(e) => {
                          register('phoneNumber').ref(e);
                          phoneInputRef.current = e;
                        }}
                        fullWidth
                        autoComplete="tel"
                        placeholder="(555) 555-5555"
                        error={!!errors.phoneNumber}
                        helperText={
                          errors.phoneNumber?.message ||
                          'Business contact number'
                        }
                        onBlur={() => trigger('phoneNumber')}
                        InputLabelProps={{ shrink: !!watch('phoneNumber') }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Tab 3: Payroll Settings */}
              {editTab === 3 && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Configure your payroll schedule. This affects how timesheets
                    are organized and when paychecks are dated.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Pay Frequency"
                        {...register('payFrequency')}
                        value={watch('payFrequency') || ''}
                        onChange={(e) =>
                          setValue('payFrequency', e.target.value)
                        }
                        fullWidth
                        error={!!errors.payFrequency}
                        helperText={
                          errors.payFrequency?.message ||
                          'How often you pay contractors'
                        }
                      >
                        {PAY_FREQUENCIES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Week Ends On"
                        {...register('endOfWeek')}
                        value={watch('endOfWeek') ?? 6}
                        onChange={(e) =>
                          setValue('endOfWeek', Number(e.target.value))
                        }
                        fullWidth
                        error={!!errors.endOfWeek}
                        helperText={
                          errors.endOfWeek?.message ||
                          'Last day of your pay period week'
                        }
                      >
                        {DAYS_OF_WEEK.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Pay Check Delay (days)"
                        type="number"
                        {...register('payCheckDelay')}
                        value={watch('payCheckDelay') ?? 1}
                        onChange={(e) =>
                          setValue('payCheckDelay', Number(e.target.value))
                        }
                        fullWidth
                        InputProps={{ inputProps: { min: 0, max: 14 } }}
                        error={!!errors.payCheckDelay}
                        helperText={
                          errors.payCheckDelay?.message ||
                          'Days after period end to issue paychecks'
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              p: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => theme.palette.custom.softBackground,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              {editTab > 0 && (
                <Button
                  onClick={() => setEditTab(editTab - 1)}
                  variant="text"
                  sx={{ textTransform: 'none' }}
                >
                  ← Previous
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={() => setEditingOpen(false)}
                variant="outlined"
                disabled={saving}
                sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                aria-label="Cancel editing (Esc)"
              >
                Cancel
              </Button>
              {editTab < 3 ? (
                <Button
                  onClick={() => setEditTab(editTab + 1)}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Next →
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  disabled={saving}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  aria-label="Save changes (Ctrl+S)"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
